import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import type { StageCount } from '../services/leadStatsApi';

interface FunnelPipelineProps {
  data: StageCount[];
  selectedAgent: string | null;
}

export default function FunnelPipeline({ data, selectedAgent }: FunnelPipelineProps) {
  const stages = useMemo(() => {
    // First: collect all unique stages with their metadata
    const stageInfo = new Map<string, { name: string; color: string; position: number }>();
    for (const d of data) {
      if (!stageInfo.has(d.stage_name)) {
        stageInfo.set(d.stage_name, { name: d.stage_name, color: d.stage_color, position: d.position });
      }
    }

    // Second: sum counts based on filter
    const result = Array.from(stageInfo.values()).map(info => {
      let count = 0;
      for (const d of data) {
        if (d.stage_name !== info.name) continue;
        if (selectedAgent) {
          if (d.agent_id === selectedAgent) count += d.count;
        } else {
          // When showing all, sum all agent entries (skip 'all' which are 0-count placeholders)
          if (d.agent_id !== 'all') count += d.count;
        }
      }
      return { ...info, count };
    });

    return result.sort((a, b) => a.position - b.position);
  }, [data, selectedAgent]);

  const maxCount = Math.max(...stages.map(s => s.count), 1);
  const totalLeads = stages.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-gray-900">Embudo de ventas</h3>
        <span className="text-xs text-gray-400">{totalLeads.toLocaleString()} leads en pipeline</span>
      </div>

      <div className="flex items-stretch gap-1">
        {stages.map((stage, i) => {
          const pct = (stage.count / maxCount) * 100;
          return (
            <div key={stage.name} className="flex items-center flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                  <span className="text-xs font-medium text-gray-700 truncate">{stage.name}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-2">{stage.count.toLocaleString()}</p>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(pct, stage.count > 0 ? 4 : 0)}%`,
                      backgroundColor: stage.color,
                    }}
                  />
                </div>
              </div>
              {i < stages.length - 1 && (
                <ChevronRight className="w-4 h-4 text-gray-300 mx-1 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
